import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import UserAppInviteList from "./UserAppInviteList";

const AppInviteForm = ({ user }) => {
  const alert = useAlert();
  const [isLoading, setIsLoading] = useState(true);
  const [inviters, setInviters] = useState([]);
  const [invitees, setInvitees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [canGenerate, setCanGenerate] = useState(false);
  const [disabledReason, setDisabledReason] = useState("");

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setInviters([]);
    setInvitees([]);

    const load = async () => {
      try {
        const { data } = await axios.get(`/api/users/${user.id}/app-invites`);
        if (ignore) return;
        setInviters(data.inviters || []);
        setInvitees(data.invitees || []);
        setCanGenerate(!!data.can_generate);
        setDisabledReason(data.disabled_reason || "");
      } catch (err) {
        console.error("Failed to load app invites", err);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    if (user?.id) load();
    return () => {
      ignore = true;
    };
  }, [user?.id]);

  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  const pushInviterRow = (invite) => {
    const inviter = invite?.inviter_dao_user || {};
    const inviterName =
      inviter.full_name ||
      [inviter.first_name, inviter.last_name].filter(Boolean).join(" ") ||
      inviter.name ||
      `User #${invite.inviter_dao_user_id}`;
    const inviterId = inviter.id ?? invite.inviter_dao_user_id;

    setInviters((prev) => [
      {
        id: invite.id,
        name: `${inviterName} (${inviterId}) (DAO)`,
        code: invite.code,
        status: cap(invite.status || "unclaimed"),
      },
      ...prev,
    ]);
  };

  const handleGenerateCode = async () => {
    if (!canGenerate) {
      return;
    }

    try {
      setSubmitting(true);

      const { data } = await axios.post("/api/invite-codes", {
        invitee_dao_user_id: user?.id,
      });

      pushInviterRow(data);
      setCanGenerate(false);
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.invite) {
        pushInviterRow(e.response.data.invite);
        setCanGenerate(false);
      } else if (e?.response?.status === 422) {
        const errorMessage =
          e?.response?.data?.message ||
          "Please add a mobile number to generate invite code.";
        alert.show(errorMessage);
      } else {
        console.error("Failed to generate invite code:", e);
        alert.show("Failed to generate invite code. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {!isLoading && (
        <UserAppInviteList
          inviters={inviters}
          invitees={invitees}
          onGenerateCode={handleGenerateCode}
          submitting={submitting}
          disableClick={!canGenerate}
          disabledReason={disabledReason}
        />
      )}
    </div>
  );
};

export default AppInviteForm;
